import React, { useState } from "react";
import {
  Box,
  Typography,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import BannerSection from "../../components/common/BannerSection";
import { Images } from "../../utils/Images";
import FaqWithTabs from "../../components/common/Faq";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";

const FaqSection = () => {
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(
    "(min-width: 900px) and (max-width: 1200px)"
  );
  // Custom media query for screens below 900px
  const isSmallScreen = useMediaQuery("(max-width: 899px)");
  // Custom media query for mobile screens (600px and below)
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isBelow1300 = useMediaQuery("(max-width: 1300px)");

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  React.useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const { donorFaqs, ngoFaqs, medicalFaqs } = React.useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const mapFaq = (id) => {
      const sec = data.find((s) => s?.id === id);
      const rel = Array.isArray(sec?.relatedCMS) ? sec.relatedCMS : [];
      return rel.map((r) => ({ question: r?.title || "", __html: r?.description || "" }));
    };
    return {
      donorFaqs: mapFaq(74),
      ngoFaqs: mapFaq(75),
      medicalFaqs: mapFaq(76),
    };
  }, [cmsHome]);

  // Tab configuration
  const tabs = [
    { label: "Donors", value: "donors" },
    { label: "NGOs", value: "ngos" },
    { label: "Medical Campaign", value: "medical" },
  ];

  return (
    <Box sx={{ marginBottom: isMobile ? "50px" : "90px" }}>
      {/* Banner Section */}
      <Box sx={{ mb: isMobile ? "10px" : "40px", mt: isMobile ? "0" : "30px" }}>
        <BannerSection title="FAQ's" subtitle="" />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          alignItems: "stretch",
          gap: 4,
          bgcolor: "#fff",
          width: "94.44%",
          maxWidth: "1440px",
          m: "30px auto 0 auto",
          borderRadius: "20.438px",
          border: isMobile ? "unset" : "1.022px solid #EAEAF0",
          padding: isMobile ? "unset" : isBelow1300 ? "20px 22px" : "40px 44px",
        }}
      >
        {/* Left Side */}
        <Box
          sx={{
            minWidth: isBelow1300 ? "450px" : "559px",
            flex: 1,
            display: isSmallScreen ? "none" : "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: isBelow1300 ? "12px" : "14px",
                color: "#131313",
                fontWeight: 400,
                letterSpacing: "0.1em",
                mb: isBelow1300 ? "10px" : "19px",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#A9C74E",
                  fontSize: "20px",
                  lineHeight: 1,
                }}
              >
                ●
              </Box>
              faq
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: isBelow1300 ? "38px" : "50px",
                color: "#000",
                lineHeight: isBelow1300 ? "50px" : "62.9px",
                letterSpacing: "-1px",
              }}
            >
              Frequently Asked{" "}
              <Box component="span" sx={{ color: "#A9C74E" }}>
                Questions
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: 2,
                fontSize: isBelow1300 ? "14px" : "16px",
                color: "#6F6C90",
                maxWidth: "420px",
              }}
            >
              Have questions about our work and impact? Find clear answers that
              help you understand how Truehope Foundation makes a difference.
            </Typography>
          </Box>

          {/* Bottom Image */}
          <Box
            component="img"
            src={Images.faqBanner}
            alt="FAQ Illustration"
            sx={{
              width: "100%",
              maxWidth: 400,
              mt: { xs: 3, md: 6 },
              mx: { xs: "auto", md: 0 },
            }}
          />
        </Box>

        {/* Right Side */}
        <Box
          sx={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
            gap: "30px",
          }}
        >
          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={handleChange}
            sx={{
              border: "1px solid #EAEAF0",
              borderRadius: "10px",
              padding: "10px",
              display: "flex",
              justifyContent: "space-between",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                color: "#4A4A49 ",
                fontSize: isBelow1300 ? "12px" : "14px",
                minHeight: isMobile ? "36px" : isBelow1300 ? "40px" : "52px",
                width: isMobile ? "auto" : "calc(33.333%)",
                flex: isMobile ? "auto" : "1",
              },
              "& .Mui-selected": {
                color: "#fff !important",
                bgcolor: "#9CBA4D ",
                borderRadius: "10px",
                padding: isMobile ? "0" : "10px",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab key={tab.value} label={tab.label} />
            ))}
          </Tabs>

          {/* FAQ Content */}
          <Box>
            {tabValue === 0 && <FaqWithTabs tab="donors" items={donorFaqs} />}
            {tabValue === 1 && <FaqWithTabs tab="ngos" items={ngoFaqs} />}
            {tabValue === 2 && <FaqWithTabs tab="medical" items={medicalFaqs} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FaqSection;
